import React, { useEffect, useState, useCallback } from "react";
import { ArrowRight, ArrowLeft, X, Compass } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

// A lightweight first-visit spotlight walkthrough for the coach dashboard.
// Anchors to on-page data-testids, scrolls each target into view, and dims the
// rest of the screen. Steps whose target isn't on the page are skipped, so it
// never points at empty air. Fires once, then persists its dismissal.
const STEPS = [
  {
    target: "coach-quickstart",
    title: "Start here",
    body: "This checklist walks you through everything you need to set up. It ticks itself off as you go.",
  },
  {
    target: "coach-momentum",
    title: "Your momentum",
    body: "Your coaching streak and milestone badges live here — a quick reason to keep showing up.",
  },
  {
    target: "coach-tabs",
    title: "Switch your tools",
    body: "Jump between the roster overview and your coaching tools — 1RM board, load calculator, volume report and more.",
  },
  {
    target: "coach-widgets",
    title: "Today's signal",
    body: "Today's sessions, athlete alerts, fresh PRs and unread messages — everything that needs you, at a glance.",
  },
  {
    target: "client-progress-section",
    title: "Your roster",
    body: "Every athlete sorted by who needs attention first. Tap any card to open their file or send a check-in.",
  },
];

const PAD = 8;

export default function CoachTour({ enabled }) {
  const { setUser } = useAuth();
  const [active, setActive] = useState(false);
  const [steps, setSteps] = useState([]);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);

  // Launch once the dashboard has painted; only keep steps whose target exists.
  useEffect(() => {
    if (!enabled) return;
    const t = setTimeout(() => {
      const present = STEPS.filter((s) => document.querySelector(`[data-testid="${s.target}"]`));
      if (present.length) {
        setSteps(present);
        setIdx(0);
        setActive(true);
      }
    }, 700);
    return () => clearTimeout(t);
  }, [enabled]);

  const measure = useCallback(() => {
    const s = steps[idx];
    if (!s) return;
    const el = document.querySelector(`[data-testid="${s.target}"]`);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [steps, idx]);

  useEffect(() => {
    if (!active) return;
    const s = steps[idx];
    if (!s) return;
    const el = document.querySelector(`[data-testid="${s.target}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(measure, 450);
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
    };
  }, [active, idx, steps, measure]);

  const finish = async () => {
    setActive(false);
    setUser((u) => ({
      ...u,
      onboarding_dismissed: { ...(u?.onboarding_dismissed || {}), coach_tour: true },
    }));
    try {
      await api.post("/onboarding/hint/dismiss", { key: "coach_tour" });
    } catch { /* non-blocking */ }
  };

  if (!active || !steps.length) return null;

  const step = steps[idx];
  const isLast = idx === steps.length - 1;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // Tooltip placement: below the target if there's room, else above.
  let tipStyle = { maxWidth: 340, width: "calc(100vw - 32px)" };
  if (rect) {
    const spaceBelow = vh - (rect.top + rect.height);
    const placeBelow = spaceBelow > 220 || spaceBelow > rect.top;
    const top = placeBelow ? rect.top + rect.height + PAD + 6 : Math.max(16, rect.top - 6 - 200);
    let left = rect.left + rect.width / 2 - 170;
    left = Math.max(16, Math.min(left, vw - 340 - 16));
    tipStyle = { ...tipStyle, top, left };
  } else {
    tipStyle = { ...tipStyle, top: vh / 2 - 100, left: vw / 2 - 170 };
  }

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="coach-tour">
      {/* Spotlight hole via huge box-shadow; click anywhere dark = skip */}
      {rect ? (
        <div
          onClick={finish}
          className="absolute rounded-md transition-all duration-300 pointer-events-auto"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: "0 0 0 9999px rgba(10,10,15,0.72)",
            border: "2px solid var(--brand-accent, #FF6B35)",
          }}
        />
      ) : (
        <div onClick={finish} className="absolute inset-0 bg-[rgba(10,10,15,0.72)]" />
      )}

      {/* Tooltip card */}
      <div
        className="absolute bg-surface-DEFAULT border border-heat/40 shadow-2xl p-5 pointer-events-auto"
        style={tipStyle}
        data-testid="coach-tour-tip"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={finish}
          className="absolute top-3 right-3 text-text-secondary hover:text-white transition-colors"
          data-testid="coach-tour-skip"
          aria-label="Skip tour"
        >
          <X size={16} weight="bold" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <Compass size={14} weight="fill" className="text-heat" />
          <span className="label-tiny text-brand-accent">
            // QUICK TOUR · {idx + 1}/{steps.length}
          </span>
        </div>
        <h3 className="font-heading text-xl mb-1.5">{step.title.toUpperCase()}</h3>
        <p className="text-sm text-text-secondary leading-relaxed mb-4">{step.body}</p>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mb-4">
          {steps.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === idx ? "w-6 bg-heat" : "w-1.5 bg-white/20"
              }`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between gap-2">
          <button
            onClick={finish}
            className="label-tiny text-text-secondary hover:text-white transition-colors"
            data-testid="coach-tour-skip-text"
          >
            SKIP
          </button>
          <div className="flex items-center gap-2">
            {idx > 0 && (
              <button
                onClick={() => setIdx((i) => i - 1)}
                className="inline-flex items-center gap-1 label-tiny border border-slate-200 px-3 py-1.5 hover:border-brand-accent hover:text-brand-accent transition-colors"
                data-testid="coach-tour-back"
              >
                <ArrowLeft size={11} weight="bold" /> BACK
              </button>
            )}
            <button
              onClick={() => (isLast ? finish() : setIdx((i) => i + 1))}
              className="btn-kinetic inline-flex items-center gap-1 px-3 py-1.5 text-xs label-tiny tracking-widest"
              data-testid="coach-tour-next"
            >
              {isLast ? "GOT IT" : "NEXT"} <ArrowRight size={11} weight="bold" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
